import { BusinessProfileId, formatPhoneNumber, UserId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import React from "react";
import { useUser } from "store/selectors/hooks";
import { businessDisplayName, toBusinessProfileDetails } from "./business-profile-types";
import { callingStatusOf, customerTrustProductStyles } from "./calling-status";
import { StatusLabel } from "./status-panels";
import { BusinessProfileOutput } from "./use-business-verification";

type PhoneLineOutput =
    inferRouterOutputs<AppRouter>["businessProfile"]["listPhoneLines"][number];

const UserName: React.FC<{ userId: UserId }> = ({ userId }) => {
    const user = useUser(userId);
    return <>{user?.name ?? "Unknown user"}</>;
};

const BusinessCells: React.FC<{ profile: BusinessProfileOutput | null }> = ({
    profile,
}) => (
    <>
        <td className="py-2 pr-4">
            {profile ? businessDisplayName(toBusinessProfileDetails(profile)) : ""}
        </td>
        <td className="py-2">
            {profile ? (
                <StatusLabel
                    style={
                        customerTrustProductStyles[callingStatusOf(profile.trustProducts)]
                    }
                />
            ) : null}
        </td>
    </>
);

const profileFor = (
    profiles: BusinessProfileOutput[],
    businessProfileId: BusinessProfileId,
): BusinessProfileOutput | null => profiles.find(p => p.id === businessProfileId) ?? null;

const PhoneLinesTable: React.FC<{
    lines: PhoneLineOutput[];
    profiles: BusinessProfileOutput[];
}> = ({ lines, profiles }) => (
    <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
            <tr className="text-left text-gray-700">
                <th scope="col" className="py-2 pr-4 font-medium">
                    Number
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                    User
                </th>
                <th scope="col" className="py-2 pr-4 font-medium">
                    Registered under
                </th>
                <th scope="col" className="py-2 font-medium">
                    Calling
                </th>
            </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
            {lines.map(line => (
                <tr key={line.id} className="text-gray-900">
                    <td className="py-2 pr-4">{formatPhoneNumber(line.phoneNumber)}</td>
                    <td className="py-2 pr-4">
                        <UserName userId={line.userId} />
                    </td>
                    <BusinessCells
                        profile={profileFor(profiles, line.businessProfileId)}
                    />
                </tr>
            ))}
        </tbody>
    </table>
);

export const PhoneLinesCard: React.FC<{ profiles: BusinessProfileOutput[] }> = ({
    profiles,
}) => {
    const trpcClient = trpc.useTRPC();
    const linesQuery = useQuery(
        trpcClient.businessProfile.listPhoneLines.queryOptions({}),
    );

    if (linesQuery.isError) {
        return (
            <RetryableErrorBanner
                message="We couldn't load your phone lines."
                retrying={linesQuery.isFetching}
                onRetry={() => {
                    linesQuery.refetch().catch((): void => undefined);
                }}
            />
        );
    }
    const lines = linesQuery.data ?? [];
    if (lines.length === 0) {
        return null;
    }
    return (
        <div className="rounded-lg border border-gray-200 p-4 space-y-2">
            <span className="font-medium text-gray-900">Phone lines</span>
            <div className="overflow-x-auto">
                <PhoneLinesTable lines={lines} profiles={profiles} />
            </div>
        </div>
    );
};
